<template>
	<TheHeader></TheHeader>

	<div class="container-fluid">
		<div class="container">
			<form name="form">
				<div class="contain row">
					<TheSidebar></TheSidebar>

					<ThePageLoading v-if="isPageLoading"></ThePageLoading>

					<div class="page-container card col-md-8" v-if="!isPageLoading">
						<ThePageHeader @import-event="importFile" @export-event="exportFile" :renderImport="!isLoading" :renderExport="!isLoading" :isImporting="isImporting" :isExporting="isExporting"></ThePageHeader>

						<div class="row" v-if="pageError == ''">
							<div class="col-md-12 p-4">
								<div class="row mb-3">
									<div class="col-md-3 required">
										<label class="col-form-label-title">Month:</label>
									</div>

									<div class="col-md-6">
										<Calendar v-model="date" view="month" dateFormat="M yy" showIcon />
									</div>
								</div>

								<div class="row mb-3">
									<Divider>
										<b>Exchange Rate</b>
									</Divider>
								</div>

								<div class="row mb-3">
									<div class="col-md-3 required">
										<label class="col-form-label-title">Currency:</label>
									</div>

									<div class="col-md-6">
										<Dropdown :options="currencies" optionLabel="code" optionValue="identifier" filter :filterFields="['code', 'name']" showClear placeholder="-- Please Select --" v-model="currencyExchangeRate.currency.identifier" :disabled="date == null" @change="onChangeCurrency">
											<template #value="slotProps">
												<div v-if="slotProps.value" class="d-flex align-items-center">
													<span class="col-md-3">{{ filterCurrencyCodeByValue(slotProps.value) }}</span>
													<span class="col-md-9">{{ filterCurrencyNameByValue(slotProps.value) }}</span>
												</div>
												<span v-else>
													{{ slotProps.placeholder }}
												</span>
											</template>

											<template #option="slotProps">
												<div class="d-flex align-items-center col-md-12">
													<span class="col-md-3">{{ slotProps.option.code }}</span>
													<span class="col-md-9">{{ slotProps.option.name }}</span>
												</div>
											</template>
										</Dropdown>
									</div>
								</div>

								<div class="row">
									<div class="col-md-6">
										<div class="row mb-3">
											<div class="col-md-6 required">
												<label class="col-form-label-title">Closing Rate:</label>
											</div>

											<div class="col-md-6">
												<InputNumber class="p-inputnumber" placeholder="Closing Rate" v-model="currencyExchangeRate.closingExchangeRate" :minFractionDigits="6" :maxFractionDigits="6" :disabled="date == null" @update:modelValue="setDefaultAverageExchangeRate" />
											</div>
										</div>

										<div class="row mb-3">
											<div class="col-md-6 required">
												<label class="col-form-label-title">Average Rate:</label>
											</div>

											<div class="col-md-6">
												<InputNumber class="p-inputnumber" placeholder="Average Rate" v-model="currencyExchangeRate.averageExchangeRate" :minFractionDigits="6" :maxFractionDigits="6" :disabled="date == null" />
											</div>
										</div>
									</div>
									
									<div class="col-md-6">
										<span class="col-form-info-title">The exchange rate is based on the MYR currency.</span>
										<br>
										<span class="col-form-info-title">Example: </span>
										<ul class="pl-2 ml-2 mt-0 col-form-info">
											<li>1 USD = 4.700000 MYR (Please key in "4.700000" as the exchange rate)</li>
											<li>1 CNY = 0.649234 MYR (Please key in "0.649234" as the exchange rate)</li>
										</ul>
									</div>
								</div>

								<div class="row mb-3">
									<div class="col-md-3"></div>

									<div class="d-flex col-md-6">
										<Button class="p-button-sm" :disabled="v$.currencyExchangeRate.$invalid || isLoading && date == null" @click="currencyExchangeRate.identifier == null ? save() : update()" label="Save" />
										<span class="p-1"></span>
										<Button class="p-button-sm p-button-secondary" @click="reset" label="Reset" />

										<TheLoading v-if="isLoading"></TheLoading>
									</div>
								</div>

								<div class="row mb-3">
									<div class="col-md-12 pt-4">
										<TheTableLoading v-if="isExchangeRateTableLoading"></TheTableLoading>

										<DataTable :value="currencyExchangeRates" class="exchangeRateTable" v-if="!isExchangeRateTableLoading" @rowClick="selectCurrencyRate">
											<Column style="width: 15%" field="currency.code" header="Code"></Column>
											<Column style="width: 45%" field="currency.name" header="Name"></Column>

											<Column style="width: 15%" field="closingExchangeRate" header="Closing Rate"></Column>
											<Column style="width: 15%" field="averageExchangeRate" header="Average Rate"></Column>
											<Column style="width: 10%" header="">
												<template #body="slotProps">
													<Button icon="pi pi-trash" severity="danger" text rounded @click="deleteExchangeRate(slotProps.data)" title="Delete" />
												</template>
											</Column>

											<template #empty>
												<div class="error-no-record mt-3">
													<FontAwesomeIcon :icon="['fas', 'image']" size="2x" />
													<h6><span>No Record Found</span></h6>
												</div>
											</template>
										</DataTable>
									</div>
								</div>
							</div>
						</div>

						<ThePageError @back-event="cancel" :pageError="pageError" v-if="pageError != ''"></ThePageError>
					</div>

					<TheFooter></TheFooter>
				</div>
			</form>
		</div>
	</div>

	<TheInvalidImportDialog title="Currency Exchange Rate" :visible="isDialogVisible" :data="invalidData" @close-dialog-event="closeDialog" @proceed-import-event="proceedImport"></TheInvalidImportDialog>

</template>

<script lang="ts">
	import { defineComponent } from "vue";
	import { useRoute } from "vue-router";
	import store from "@/store";
	import currencyApi from "@/api/currency-api";
	import currencyExchangeRateApi from "@/api/currencyexchangerate-api";
	import statusMixin from "@/mixin/status";
	import dateUtilsMixin from "@/mixin/utils/date-utils";

	import TheHeader from "@/components/TheHeader.vue";
	import TheFooter from "@/components/TheFooter.vue";
	import TheSidebar from "@/components/TheSidebar.vue";
	import ThePageHeader from "@/components/ThePageHeader.vue";
	import ThePageLoading from "@/components/ThePageLoading.vue";
	import TheTableLoading from "@/components/TheTableLoading.vue";
	import TheLoading from "@/components/TheLoading.vue";
	import ThePageError from "@/components/ThePageError.vue";
	import TheInvalidImportDialog from "@/components/TheInvalidImportDialog.vue";
	
	import useVuelidate from "@vuelidate/core";
	import { required } from "@vuelidate/validators";

	export default defineComponent({
		mixins: [statusMixin, dateUtilsMixin],
		components: {
			TheHeader,
			TheFooter,
			TheSidebar,
			ThePageHeader,
			ThePageLoading,
			TheTableLoading,
			TheLoading,
			ThePageError,
			TheInvalidImportDialog,
		},
		data() {
			return {
				pageError: (store.getters.error == undefined ? '' : store.getters.error),
				currencies: [] as any[],
				currency: {identifier: null},
				currencyExchangeRate: {identifier: null, currency: {identifier: null}, closingExchangeRate: null as any, averageExchangeRate: null as any, year: null as any, month: null as any, isClosed: false},
				currencyExchangeRates: [] as any[],
				date: null as any,
				selectedDate: null as any,
				isPageLoading: false,
				isLoading: false,
				isDeleting: false,
				isExchangeRateTableLoading: false,
				isImporting: false,
				isExporting: false,
				isDialogVisible: false,
				invalidData: {} as any
			};
		},
		mounted: function () {
			if (this.pageError == '') {
				this.isPageLoading = true;

				currencyApi.currencyList({})
					.then(response => {
						this.currencies = response.data.listSet;
						this.isPageLoading = false;
					})
					.catch(error => {
						this.pageError = error;
						this.isPageLoading = false;
					});
			}
		},
		watch: {
			date(value: any) {
				this.selectedDate = new Date(value);
				
				this.reset();
				this.listCurrencyExchangeRate();
			}
		},
		methods: {
			save() {
				this.isLoading = true;

				this.currencyExchangeRate.year = this.selectedDate.getFullYear();
				this.currencyExchangeRate.month = this.selectedDate.getMonth() + 1;

				currencyExchangeRateApi.currencyExchangeRateSave({currencyExchangeRate: this.currencyExchangeRate})
					.then((response: any) => {
						store.getters.getCached.page = null;
						this.$toast.add({severity: "success", summary: "Success", detail: response.message, life: 6000});
						this.reset();
						this.listCurrencyExchangeRate();

						this.isLoading = false;
					})
					.catch(error => {
						this.isLoading = false;
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
					});
			},
			update() {
				this.isLoading = true;
				currencyExchangeRateApi.currencyExchangeRateUpdate({currencyExchangeRate: this.currencyExchangeRate})
					.then((response: any) => {
						this.isLoading = false;
						this.$toast.add({severity: "success", summary: "Success", detail: response.message, life: 6000});
						this.reset();
						this.listCurrencyExchangeRate();
					})
					.catch(error => {
						this.isLoading = false;
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
					});
			},
			reset() {
				this.currencyExchangeRate = {identifier: null, currency: {identifier: null}, closingExchangeRate: null as any, averageExchangeRate: null as any, year: null as any, month: null as any, isClosed: false};
			},
			deleteExchangeRate(exchangeRate: any) {
				this.$confirm.require({
					header: 'Confirmation',
					message: 'Are you sure you want to delete this exchange rate?',
					icon: 'pi pi-exclamation-triangle',
					accept: () => {
						this.isLoading = true;
						currencyExchangeRateApi.currencyExchangeRateDelete({currencyExchangeRate: exchangeRate})
							.then((response: any) => {
								this.isLoading = false;
								this.$toast.add({severity: "success", summary: "Success", detail: response.message, life: 6000});
								this.reset();
								this.listCurrencyExchangeRate();
							})
							.catch(error => {
								this.isLoading = false;
								this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
							});
					}
				});
			},
			onChangeCurrency(event: any) {
				var filteredIndex = this.currencyExchangeRates.findIndex((exchangeRate: any) => {
					return exchangeRate.currency.identifier == event.value;
				});

				if (filteredIndex != -1) {
					this.currencyExchangeRate.identifier = this.currencyExchangeRates[filteredIndex].identifier;
					this.currencyExchangeRate.currency = {...this.currencyExchangeRates[filteredIndex].currency};
					this.currencyExchangeRate.closingExchangeRate = Number(this.currencyExchangeRates[filteredIndex].closingExchangeRate);
					this.currencyExchangeRate.averageExchangeRate = Number(this.currencyExchangeRates[filteredIndex].averageExchangeRate);
					this.currencyExchangeRate.year = this.currencyExchangeRates[filteredIndex].year;
					this.currencyExchangeRate.month = this.currencyExchangeRates[filteredIndex].month;
				}

				else {
					this.currencyExchangeRate = {identifier: null, currency: {identifier: event.value}, closingExchangeRate: null as any, averageExchangeRate: null as any, year: null as any, month: null as any, isClosed: false};
				}
			},
			selectCurrencyRate(event: any) {
				this.currencyExchangeRate.identifier = event.data.identifier;
				this.currencyExchangeRate.currency = {...event.data.currency};
				this.currencyExchangeRate.closingExchangeRate = Number(event.data.closingExchangeRate);
				this.currencyExchangeRate.averageExchangeRate = Number(event.data.averageExchangeRate);
				this.currencyExchangeRate.year = event.data.year;
				this.currencyExchangeRate.month = event.data.month;

				var currencyIndex = this.currencies.findIndex((currency: any) => {
					return currency.identifier == this.currencyExchangeRate.currency.identifier;
				})
				if (currencyIndex == -1) {
					this.currencyExchangeRate.currency = {identifier: null};
				}
			},
			listCurrencyExchangeRate() {
				this.isExchangeRateTableLoading = true;
				currencyExchangeRateApi.currencyExchangeRateList({year: this.selectedDate.getFullYear(), month: this.selectedDate.getMonth() + 1})
					.then(response => {
						this.currencyExchangeRates = response.data.listSet;
						this.isExchangeRateTableLoading = false;
					})
					.catch(error => {
						this.currencyExchangeRates = [] as any[];
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
						this.isExchangeRateTableLoading = false;
					});
			},
			setDefaultAverageExchangeRate() {
				if (this.currencyExchangeRate.averageExchangeRate == null) {
					this.currencyExchangeRate.averageExchangeRate = this.currencyExchangeRate.closingExchangeRate;
				}
			},
			filterCurrencyCodeByValue: function (value: any) {
				var currencies = this.currencies.filter(function (currency: any) {
					if (currency.identifier == value) {
						return currency;
					}
				});

				if (currencies != null && currencies.length > 0) {
					return currencies[0].code;
				}
				return null;
			},
			filterCurrencyNameByValue: function (value: any) {
				var currencies = this.currencies.filter(function (currency: any) {
					if (currency.identifier == value) {
						return currency;
					}
				});

				if (currencies != null && currencies.length > 0) {
					return currencies[0].name;
				}
				return null;
			},
			importFile(file: any) {
				this.isImporting = true;

				var formData = new FormData();
				formData.append('file', file[0]);

				currencyExchangeRateApi.currencyExchangeRateImport(formData)
					.then((response: any) => {
						this.isImporting = false;

						if (response.data == null || response.data == '') {
							this.$toast.add({severity: "success", summary: "Success", detail: response.message, life: 6000});
						}

						else {
							this.invalidData = response.data;
							this.isDialogVisible = true;
						}
					})
					.catch(error => {
						this.isImporting = false;
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
					});
			},
			proceedImport(isOverride: boolean) {
				this.isImporting = true;

				let tempInvalidData = JSON.parse(JSON.stringify(this.invalidData));

				if (isOverride == false && tempInvalidData.warningCount > 0) {
					tempInvalidData.invalidRows.forEach((invalidRow: any, index: any) => {
						if (invalidRow.type == "Warning") {
							invalidRow.type = "Error";
							return invalidRow;
						}
					});
				}

				currencyExchangeRateApi.currencyExchangeRateImportProceed({'invalidData': tempInvalidData})
					.then((response: any) => {
						this.isImporting = false;
						this.$toast.add({severity: "success", summary: "Success", detail: response.message, life: 6000});
						this.isDialogVisible = false;
						if (this.selectedDate != null) {
							this.reset();
							this.listCurrencyExchangeRate();
						}
					})
					.catch(error => {
						this.isImporting = false;
						this.isDialogVisible = false;
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
					});
			},
			exportFile() {
				this.isExporting = true;

				currencyExchangeRateApi.currencyExchangeRateExport({})
					.then(response => {
						this.isExporting = false;
					})
					.catch(error => {
						this.isExporting = false;
						this.$toast.add({severity: "error", summary: "Error", detail: error, life: 6000});
					});
			},
			closeDialog() {
				this.isDialogVisible = false;
			}
		},
		setup() {
			return {v$: useVuelidate()};
		},
		validations() {
			return {
				currencyExchangeRate: {
					currency: {
						identifier: {
							required
						}
					},
					closingExchangeRate: {
						required
					},
					averageExchangeRate: {
						required
					},
					isClosed: {
						required
					}
				},
			};
		}
	});
</script>
